import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck, ScrollText, BookOpen, ExternalLink } from 'lucide-react';

/**
 * Public rodapé shown on the tenant's public whistleblowing pages
 * (report form, tracking page, policy pages). Reads the tenant's branding
 * response and renders links to the 4 configurable documents:
 *  - Política de Privacidade
 *  - Termos de Utilização
 *  - Código de Ética
 *  - PPRCIC
 *
 * For "privacy" and "terms" we prefer routing to an internal page (that
 * renders the stored content); for "ethics" and "pprcic" the tenant provides
 * a direct URL or PDF link — those open in a new tab.
 */
const TenantFooter = ({ branding }) => {
  if (!branding) return null;
  const {
    slug,
    name,
    has_privacy_policy,
    has_terms_of_use,
    privacy_policy_url,
    terms_of_use_url,
    code_of_ethics_url,
    code_of_ethics_pdf_url,
    pprcic_url,
    pprcic_pdf_url,
  } = branding;

  // Ethics/PPRCIC: PDF has priority over external URL
  const ethicsHref = code_of_ethics_pdf_url || code_of_ethics_url;
  const pprcicHref = pprcic_pdf_url || pprcic_url;

  const hasAnything =
    has_privacy_policy || has_terms_of_use || ethicsHref || pprcicHref;

  if (!hasAnything) {
    // Minimal footer — no docs configured
    return (
      <footer className="mt-16 border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-slate-500 text-center">
          <span className="font-medium text-slate-700">CogniEtica</span>
          {name && <> — {name}</>} · Canal em conformidade com a Lei n.º 93/2021
        </div>
      </footer>
    );
  }

  return (
    <footer className="mt-16 border-t border-slate-200 bg-slate-50" data-testid="tenant-footer">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {has_privacy_policy && (
            <FooterLink
              testid="footer-privacy"
              icon={ShieldCheck}
              label="Política de Privacidade"
              // If tenant provided only an external URL, honour it directly
              href={privacy_policy_url && !branding.privacy_policy_content ? privacy_policy_url : null}
              to={!privacy_policy_url || branding.privacy_policy_content ? `/privacidade/${slug}` : null}
            />
          )}
          {has_terms_of_use && (
            <FooterLink
              testid="footer-terms"
              icon={ScrollText}
              label="Termos de Utilização"
              href={terms_of_use_url && !branding.terms_of_use_content ? terms_of_use_url : null}
              to={!terms_of_use_url || branding.terms_of_use_content ? `/termos/${slug}` : null}
            />
          )}
          {ethicsHref && (
            <FooterLink
              testid="footer-ethics"
              icon={BookOpen}
              label="Código de Ética"
              href={ethicsHref}
            />
          )}
          {pprcicHref && (
            <FooterLink
              testid="footer-pprcic"
              icon={FileText}
              label="PPRCIC"
              href={pprcicHref}
            />
          )}
        </div>

        <div className="mt-8 pt-4 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-slate-500">
          <div>
            <span className="font-medium text-slate-700">CogniEtica</span>
            {name && <> — {name}</>}
          </div>
          <div>Canal em conformidade com a Lei n.º 93/2021 e Diretiva UE 2019/1937</div>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ icon: Icon, label, href, to, testid }) => {
  const cls =
    'group flex items-start gap-3 rounded-lg border border-transparent p-3 hover:bg-white hover:border-slate-200 transition';
  const inner = (
    <>
      <Icon className="w-5 h-5 mt-0.5 text-indigo-600 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 group-hover:text-indigo-700 flex items-center gap-1.5">
          {label}
          {href && <ExternalLink className="w-3 h-3 opacity-60" />}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testid}>
      {inner}
    </Link>
  );
};

export default TenantFooter;
